import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  completeVerificationStep,
  startWhatsAppReverse,
  getWhatsAppReverseStatus,
  getWhatsAppConfig,
  sendWhatsAppCode,
} from '@/apis/experience/verification-setup';
import Button from '@/components/Button';
import { InputField } from '@/components/InputFields';
import VerificationCodeInput from '@/components/VerificationCode';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';

import type { VerificationStepProps } from '../types';

import styles from './WhatsAppVerificationStep.module.scss';

/** How often the browser polls for the inbound message to arrive (reverse-OTP mode). */
const pollIntervalMs = 2500;

/**
 * WhatsApp verification step. Two modes, selected by the server flag `reverseOtpEnabled`
 * (env `WHATSAPP_REVERSE_OTP_ENABLED`):
 *
 * - Reverse-OTP (enabled): the user taps a wa.me deep link and sends a free inbound message; the
 *   backend webhook verifies it and the browser polls until verified. No billable send.
 * - Legacy forward OTP (disabled, default): the server sends a code via the AdMedia connector and
 *   the user types it back — the original behaviour, kept working alongside the reverse flow.
 *
 * NOTE (reverse mode): a bundled QR renderer (e.g. `qrcode.react`) should display `waMeUrl` for
 * desktop scanning. Omitted to avoid leaking the token through a third-party QR image service.
 */
const WhatsAppVerificationStep = ({ step, onStepComplete, progress }: VerificationStepProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  // undefined while loading the mode; then true (reverse) or false (legacy forward).
  const [reverseEnabled, setReverseEnabled] = useState<boolean | undefined>();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reverse-OTP state.
  const [waMeUrl, setWaMeUrl] = useState<string | undefined>();
  const [isExpired, setIsExpired] = useState(false);

  // Legacy forward-OTP state.
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>([]);

  const asyncStart = useApi(startWhatsAppReverse);
  const asyncSendCode = useApi(sendWhatsAppCode);
  const asyncCompleteStep = useApi(completeVerificationStep);

  const pollTimer = useRef<ReturnType<typeof setInterval>>();
  const expiryTimer = useRef<ReturnType<typeof setTimeout>>();

  const stopTimers = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = undefined;
    }
    if (expiryTimer.current) {
      clearTimeout(expiryTimer.current);
      expiryTimer.current = undefined;
    }
  }, []);

  // Resolve which mode to render.
  useEffect(() => {
    void (async () => {
      try {
        const config = await getWhatsAppConfig();
        setReverseEnabled(config.reverseOtpEnabled);
      } catch {
        // Fall back to legacy forward OTP if the config can't be read.
        setReverseEnabled(false);
      }
    })();
  }, []);

  useEffect(() => stopTimers, [stopTimers]);

  const reset = useCallback(() => {
    stopTimers();
    setWaMeUrl(undefined);
    setIsExpired(false);
    setIsCodeSent(false);
    setVerificationCode([]);
  }, [stopTimers]);

  // Reverse-OTP: get the wa.me link and begin polling for the inbound message.
  const handleStartReverse = useCallback(async () => {
    if (!phoneNumber) {
      return;
    }

    setIsSubmitting(true);
    setIsExpired(false);

    const [error, result] = await asyncStart(phoneNumber);

    setIsSubmitting(false);

    if (error) {
      await handleError(error);
      return;
    }

    if (!result) {
      return;
    }

    setWaMeUrl(result.waMeUrl);

    expiryTimer.current = setTimeout(() => {
      stopTimers();
      setIsExpired(true);
    }, result.expiresInSeconds * 1000);

    pollTimer.current = setInterval(async () => {
      try {
        const status = await getWhatsAppReverseStatus();

        if (status.verified) {
          stopTimers();
          const [completeError, completeResult] = await asyncCompleteStep('whatsapp', {});

          if (completeError) {
            await handleError(completeError);
            return;
          }

          if (completeResult?.success) {
            await onStepComplete('whatsapp');
          }
        }
      } catch {
        // Transient polling errors are ignored; polling continues until verified or expiry.
      }
    }, pollIntervalMs);
  }, [phoneNumber, asyncStart, asyncCompleteStep, handleError, onStepComplete, stopTimers]);

  // Legacy forward OTP: ask the server to send a code via AdMedia.
  const handleSendCode = useCallback(async () => {
    if (!phoneNumber) {
      return;
    }

    setIsSubmitting(true);
    const [error] = await asyncSendCode(phoneNumber);
    setIsSubmitting(false);

    if (error) {
      await handleError(error);
      return;
    }

    setIsCodeSent(true);
  }, [phoneNumber, asyncSendCode, handleError]);

  // Legacy forward OTP: verify the typed code.
  const handleVerifyCode = useCallback(async () => {
    const codeString = verificationCode.join('');

    if (codeString.length !== 6) {
      return;
    }

    setIsSubmitting(true);
    const [error, result] = await asyncCompleteStep('whatsapp', {
      code: codeString,
      phone: phoneNumber,
    });
    setIsSubmitting(false);

    if (error) {
      await handleError(error);
      return;
    }

    if (result?.success) {
      await onStepComplete('whatsapp');
    }
  }, [verificationCode, phoneNumber, asyncCompleteStep, handleError, onStepComplete]);

  if (step.status === 'completed') {
    return (
      <div className={styles.completedStep}>
        <div className={styles.completedIcon}>✅</div>
        <div className={styles.completedContent}>
          <h3>{t('mfa_setup.whatsapp_verification_completed')}</h3>
          <p>{t('mfa_setup.whatsapp_verification_completed_description')}</p>
          {progress?.whatsappVerifiedAt && (
            <div className={styles.completedTime}>
              {t('mfa_setup.completed_at', {
                time: new Date(progress.whatsappVerifiedAt).toLocaleString(),
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step.status === 'pending') {
    return (
      <div className={styles.pendingStep}>
        <div className={styles.pendingIcon}>⏳</div>
        <div className={styles.pendingContent}>
          <h3>{t('mfa_setup.whatsapp_verification_pending')}</h3>
          <p>{t('mfa_setup.complete_previous_steps_first')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>{step.icon}</div>
        <div>
          <h2 className={styles.stepTitle}>{step.title}</h2>
          <p className={styles.stepDescription}>{step.description}</p>
        </div>
      </div>

      <div className={styles.stepContent}>
        {reverseEnabled === undefined ? (
          <p className={styles.instruction}>Loading…</p>
        ) : reverseEnabled ? (
          // --- Reverse-OTP mode: user sends an inbound message via the wa.me link. ---
          !waMeUrl ? (
            <div className={styles.phoneSection}>
              <p className={styles.instruction}>
                Enter your WhatsApp number to verify it by sending us a quick message.
              </p>
              <div className={styles.phoneInputSection}>
                <InputField
                  name="phoneNumber"
                  type="tel"
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={({ target: { value } }) => setPhoneNumber(value)}
                  className={styles.phoneInput}
                />
              </div>
              <Button
                title="Verify via WhatsApp"
                type="primary"
                size="large"
                isLoading={isSubmitting}
                isDisabled={!phoneNumber}
                onClick={handleStartReverse}
              />
            </div>
          ) : (
            <div className={styles.verifyCodeSection}>
              <p className={styles.instruction}>
                Tap the button below to open WhatsApp, then send the pre-filled message. This page
                updates automatically once we receive it.
              </p>
              {/* TODO: render a QR code of `waMeUrl` here for desktop scanning (add `qrcode.react`). */}
              <div className={styles.actionButtons}>
                <a href={waMeUrl} target="_blank" rel="noopener noreferrer">
                  <Button title="Open WhatsApp" type="primary" size="large" onClick={() => {}} />
                </a>
                {isExpired && (
                  <p className={styles.instruction}>This link has expired. Please try again.</p>
                )}
                <Button title="Change Phone Number" type="secondary" size="small" onClick={reset} />
              </div>
            </div>
          )
        ) : // --- Legacy forward-OTP mode: we send a code via AdMedia; user types it back. ---
        !isCodeSent ? (
          <div className={styles.phoneSection}>
            <p className={styles.instruction}>
              Enter your phone number to receive a WhatsApp verification code
            </p>
            <div className={styles.phoneInputSection}>
              <InputField
                name="phoneNumber"
                type="tel"
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={({ target: { value } }) => setPhoneNumber(value)}
                className={styles.phoneInput}
              />
            </div>
            <Button
              title="Send WhatsApp Code"
              type="primary"
              size="large"
              isLoading={isSubmitting}
              isDisabled={!phoneNumber}
              onClick={handleSendCode}
            />
          </div>
        ) : (
          <div className={styles.verifyCodeSection}>
            <p className={styles.instruction}>
              Enter the 6-digit verification code sent to your WhatsApp ({phoneNumber})
            </p>
            <div className={styles.codeInputSection}>
              <VerificationCodeInput
                name="verificationCode"
                value={verificationCode}
                onChange={(code) => setVerificationCode(code)}
                className={styles.codeInput}
              />
            </div>
            <div className={styles.actionButtons}>
              <Button
                title="Verify Code"
                type="primary"
                size="large"
                isLoading={isSubmitting}
                isDisabled={verificationCode.join('').length !== 6}
                onClick={handleVerifyCode}
              />
              <Button title="Change Phone Number" type="secondary" size="medium" onClick={reset} />
            </div>
          </div>
        )}
      </div>

      <div className={styles.stepFooter}>
        <div className={styles.helpText}>
          WhatsApp verification helps secure your account with an additional layer of protection.
          Make sure you have access to WhatsApp on the phone number you provide.
        </div>
      </div>
    </div>
  );
};

export default WhatsAppVerificationStep;
